import { Heart, SpinnerIcon, Trash, User } from "assets/svg";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FormattedDate,
  FormattedMessage,
  FormattedTime,
  useIntl,
} from "react-intl";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useEventStore,
  useGuestStore,
  useTimeToReleaseUploadStore,
  useFeedStore,
} from "store";
import CdtAppPublicApi from "cdt-app-public-api/dist";
import { clarity } from "react-microsoft-clarity";
import * as SpacesService from "libs/digitalocean/SpacesService";
import { setUploadBehaviorTags } from "utils/clarityHelpers";
import FeedHeader from "./FeedHeader";
import ScrollToTopButton from "./ScrollToTopButton";
import CreatePostModal from "./CreatePost";
import { CtaButton } from "./CtaButton";
import cdtWhiteLogo from "../../assets/images/cdt-logo-bco.png";
import TopBar from "./TopBar";
import { TFeedEvent } from "cdt-app-public-api/dist/types";
import { FeedModal } from "./FeedModal";
import GuestFormModal from "components/GuestFormModal";

export default function FeedV2() {
  const { event, theme } = useEventStore();
  const { guest, uploadStats } = useGuestStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [feedEvents, setFeedEvents] = useState<TFeedEvent[]>([]);
  const [createPost, setCreatePost] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastDocId, setLastDocId] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [guestModalOpen, setGuestModalOpen] = useState(false);
  const [shouldOpenCreatePostAfterModal, setShouldOpenCreatePostAfterModal] =
    useState(false);

  const {
    getCachedFeed,
    setCachedFeed,
    appendToFeed,
    prependNewEvents,
    updateExistingEvents,
    addOptimisticPost,
    removeEventFromCache,
    isCacheValid,
    clearFeedCache,
  } = useFeedStore();

  const EVENTS_PER_PAGE = 15;
  const EXPIRATION_TIME = 86400; // 24 hours in seconds

  const handleClosePostCreation = () => {
    setCreatePost(false);
  };

  const handleOpenCreatePost = useCallback(() => {
    if (!event?.id || !guest?.id) return;

    if (!guest?.name) {
      setGuestModalOpen(true);
      setShouldOpenCreatePostAfterModal(true);
      return;
    }

    setCreatePost(true);
  }, [event?.id, guest?.id, guest?.name]);

  const handleGuestModalClose = useCallback(() => {
    setGuestModalOpen(false);

    if (shouldOpenCreatePostAfterModal) {
      setShouldOpenCreatePostAfterModal(false);
      setCreatePost(true);
    }
  }, [shouldOpenCreatePostAfterModal]);

  const handlePostCreation = (post: TFeedEvent) => {
    if (post && event?.code) {
      addOptimisticPost(event.code, post);
      setFeedEvents((prevEvents) => {
        const newState = [post, ...prevEvents];
        return newState;
      });
    }

    setCreatePost(false);
  };

  const fetchItemUrl = useCallback(
    async (feedEvent: TFeedEvent): Promise<TFeedEvent> => {
      const updatedEvent = { ...feedEvent } as TFeedEvent;

      switch (feedEvent.type) {
        case "image-uploader":
        case "cdt-image-uploader": {
          const fileUrls: string[] = [];

          if (feedEvent.uploadFileStoragePaths?.length) {
            const uploadFileUrls = await Promise.all(
              feedEvent.uploadFileStoragePaths.map(async (path) =>
                path
                  ? await SpacesService.getFileUrl(path, EXPIRATION_TIME)
                  : null
              )
            );
            const validUrls = uploadFileUrls.filter(
              (url): url is string => url !== null
            );
            fileUrls.push(...validUrls);
          } else if (feedEvent.fileStoragePath) {
            const fileUrl = await SpacesService.getFileUrl(
              feedEvent.fileStoragePath,
              EXPIRATION_TIME
            );
            if (fileUrl) {
              fileUrls.push(fileUrl);
            }
          }

          (updatedEvent as typeof feedEvent).fileUrls = fileUrls;
          break;
        }

        case "photo-challenge-done":
          if (feedEvent.photoStoragePath) {
            (updatedEvent as typeof feedEvent).photoUrl =
              await SpacesService.getFileUrl(
                feedEvent.photoStoragePath,
                EXPIRATION_TIME
              );
          }
          break;

        case "new-post":
          if (feedEvent.postImageStoragePath) {
            (updatedEvent as typeof feedEvent).postImageUrl =
              await SpacesService.getFileUrl(
                feedEvent.thumbnailStoragePath ||
                  feedEvent.postImageStoragePath,
                EXPIRATION_TIME
              );
          }
          break;
        default:
          break;
      }

      if (feedEvent.guestPhotoStoragePath) {
        updatedEvent.guestPhotoUrl = await SpacesService.getFileUrl(
          feedEvent.guestPhotoStoragePath,
          EXPIRATION_TIME
        );
      }

      return updatedEvent;
    },
    []
  );

  useEffect(() => {
    const fetchFeedData = async () => {
      if (!event?.code) {
        throw new Error("Event code not found");
      }
      const cachedData = getCachedFeed(event.code);

      if (cachedData && isCacheValid(event.code)) {
        setFeedEvents(cachedData.events);
        setHasMore(cachedData.hasMore);
        setLastDocId(cachedData.lastDocId);
        setLoading(false);
        checkForNewEventsOnLoad();
        return;
      }
      if (cachedData && !isCacheValid(event.code)) {
        clearFeedCache(event.code);
      }

      try {
        setLoading(true);
        const cdtAppClient = new CdtAppPublicApi();

        const response = await cdtAppClient.getFeedV2(
          event.code as string,
          EVENTS_PER_PAGE
        );

        if (!response || !response.data) {
          throw new Error("No feed items found");
        }

        const {
          feedEvents: events,
          hasMore: moreAvailable,
          lastDocId: lastId,
        } = response.data;

        const eventsWithUrls = await Promise.all(events.map(fetchItemUrl));

        setFeedEvents(eventsWithUrls);
        setHasMore(moreAvailable);
        setLastDocId(lastId);

        setCachedFeed(event.code, {
          events: eventsWithUrls,
          hasMore: moreAvailable,
          lastDocId: lastId,
        });
      } catch (error) {
        console.error("Error fetching feed data:", error);
      } finally {
        setLoading(false);
      }
    };

    const checkForNewEventsOnLoad = async () => {
      if (!event?.code) return;

      try {
        const cachedData = getCachedFeed(event.code);
        if (!cachedData || cachedData.events.length === 0) return;

        const latestEventDate = new Date(cachedData.events[0].createdAt);

        const cdtAppClient = new CdtAppPublicApi();
        const response = await cdtAppClient.getFeedV2(
          event.code,
          EVENTS_PER_PAGE
        );

        if (!response?.data?.feedEvents) return;

        const serverEvents = response.data.feedEvents;

        const cachedEventIds = cachedData.events.map((e) => e.id);

        const updatedEvents = serverEvents.filter((serverEvent) =>
          cachedEventIds.includes(serverEvent.id)
        );

        const newEvents = serverEvents.filter((serverEvent) => {
          const isExisting = cachedEventIds.includes(serverEvent.id);
          const eventDate = new Date(serverEvent.createdAt);
          return !isExisting && eventDate > latestEventDate;
        });

        if (updatedEvents.length > 0) {
          const updatedEventsWithUrls = await Promise.all(
            updatedEvents.map(fetchItemUrl)
          );
          updateExistingEvents(event.code, updatedEventsWithUrls);

          setFeedEvents((currentEvents) => {
            const eventMap = new Map<string, TFeedEvent>();

            currentEvents.forEach((event) => {
              eventMap.set(event.id, event);
            });

            updatedEventsWithUrls.forEach((updatedEvent) => {
              const existing = eventMap.get(updatedEvent.id);
              if (existing) {
                const existingDate = new Date(
                  existing.updatedAt || existing.createdAt
                );
                const updatedDate = new Date(
                  updatedEvent.updatedAt || updatedEvent.createdAt
                );

                if (updatedDate >= existingDate) {
                  eventMap.set(updatedEvent.id, updatedEvent);
                }
              }
            });

            const sortedUpdatedEvents = Array.from(eventMap.values()).sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            );

            return sortedUpdatedEvents;
          });
        }

        if (newEvents.length > 0) {
          const newEventsWithUrls = await Promise.all(
            newEvents.map(fetchItemUrl)
          );

          prependNewEvents(event.code, newEventsWithUrls);

          setFeedEvents((currentEvents) => {
            const currentIds = currentEvents.map((e) => e.id);
            const uniqueNewEvents = newEventsWithUrls.filter(
              (e) => !currentIds.includes(e.id)
            );

            if (uniqueNewEvents.length === 0) return currentEvents;

            const sortedNewEvents = uniqueNewEvents.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            );

            return [...sortedNewEvents, ...currentEvents];
          });
        }
      } catch (error) {
        console.error("Error fetching new events:", error);
      }
    };

    fetchFeedData();
  }, [
    event,
    fetchItemUrl,
    isCacheValid,
    getCachedFeed,
    setCachedFeed,
    prependNewEvents,
    updateExistingEvents,
    clearFeedCache,
  ]);

  const loadMoreEvents = useCallback(async () => {
    if (!hasMore || loadingMore || !event?.code || !lastDocId) return;

    try {
      setLoadingMore(true);

      const cdtAppClient = new CdtAppPublicApi();

      const response = await cdtAppClient.getFeedV2(
        event.code as string,
        EVENTS_PER_PAGE,
        lastDocId
      );

      if (!response || !response.success || !response.data) {
        return;
      }

      const {
        feedEvents: newEvents,
        hasMore: moreAvailable,
        lastDocId: newLastDocId,
      } = response.data;

      const newEventsWithUrls = await Promise.all(newEvents.map(fetchItemUrl));

      setFeedEvents((prev) => [...prev, ...newEventsWithUrls]);
      setHasMore(moreAvailable);
      setLastDocId(newLastDocId);
      appendToFeed(event.code, newEventsWithUrls, newLastDocId, moreAvailable);
    } catch (error) {
      console.error("Error loading more events:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [
    hasMore,
    loadingMore,
    event?.code,
    lastDocId,
    fetchItemUrl,
    appendToFeed,
  ]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollContainer = scrollContainerRef.current;
      if (!scrollContainer) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      const scrollPosition = scrollTop + clientHeight;
      const threshold = scrollHeight - 200;

      if (scrollPosition >= threshold && hasMore && !loadingMore) {
        loadMoreEvents();
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      if (isIOS) {
        const events = ["scroll", "touchend", "touchmove"];

        events.forEach((eventType) => {
          scrollContainer.addEventListener(eventType, handleScroll, {
            passive: true,
          });
        });

        return () => {
          events.forEach((eventType) => {
            scrollContainer.removeEventListener(eventType, handleScroll);
          });
        };
      } else {
        scrollContainer.addEventListener("scroll", handleScroll);
        return () =>
          scrollContainer.removeEventListener("scroll", handleScroll);
      }
    }
  }, [hasMore, loadingMore, loadMoreEvents]);


  useEffect(() => {
    if (clarity.hasStarted()) {
      clarity.setTag("feed", "engaged");
      clarity.setTag("app_mode", "feed");
      clarity.setTag("engagement_level_viewer", "true");

      // Track upload behavior ranges
      setUploadBehaviorTags(uploadStats);
    }
  }, [uploadStats]);

  useEffect(() => {
    if (location.state?.openCreatePost) {
      handleOpenCreatePost();
      window.history.replaceState({}, document.title);
    }
  }, [location.state, handleOpenCreatePost]);

  const removeEventFromFeed = useCallback(
    (eventId: string) => {
      if (!event?.code) return;

      removeEventFromCache(event.code, eventId);

      setFeedEvents((prevEvents) => prevEvents.filter((e) => e.id !== eventId));
    },
    [event?.code, removeEventFromCache]
  );

  return (
    <>
      <TopBar />
      <div
        ref={scrollContainerRef}
        className={`mt-8 h-full w-full flex justify-center items-center pt-6 overflow-hidden overflow-y-auto `}
      >
        <div className="h-full w-full flex flex-col justify-start items-center px-2">
          <FeedHeader setCreatePost={handleOpenCreatePost} />
          <CreatePostModal
            createPost={createPost}
            setPost={setCreatePost}
            onClose={handleClosePostCreation}
            onPostCreated={handlePostCreation}
            frameSelector={true}
          />

          <div className="w-full flex-1">
            {loading ? (
              <LoadingState />
            ) : feedEvents.length === 0 ? (
              <EmptyState />
            ) : (
              <FeedContent
                events={feedEvents}
                theme={theme}
                navigate={navigate}
                onRemoveEventFromFeed={removeEventFromFeed}
              />
            )}
          </div>
          <ScrollToTopButton />
          {loadingMore && (
            <div className="w-full flex justify-center -mt-10 pb-4">
              <SpinnerIcon width={24} height={24} color="#fff" outline="#000" />
            </div>
          )}
        </div>
      </div>
      <GuestFormModal isOpen={guestModalOpen} onClose={handleGuestModalClose} />
    </>
  );
}

const LoadingState = () => {
  return (
    <div className="flex flex-col items-center justify-center w-full">
      <p className="text-xl text-center">
        <FormattedMessage
          id="feed.loading"
          defaultMessage="Carregando feed..."
        />
      </p>
    </div>
  );
};

const EmptyState = () => {
  return (
    <div className="flex flex-col items-center justify-center w-full">
      <p className="text-xl text-center">
        <FormattedMessage
          id="feed.empty"
          defaultMessage="Não há itens no feed ainda."
        />
      </p>
    </div>
  );
};

type TFeedContentProps = {
  events: TFeedEvent[];
  theme: { card: string; buttonSecondary: string };
  onRemoveEventFromFeed: (eventId: string) => void;
  navigate: (path: string) => void;
};

const FeedContent = ({ events, onRemoveEventFromFeed }: TFeedContentProps) => {
  return (
    <div className="w-full h-full mb-16">
      <div className="relative flex flex-col gap-4 pb-4 ">
        {events.map((feedEvent) => (
          <FeedEventCard
            key={feedEvent.id}
            event={feedEvent}
            onRemoveEventFromFeed={onRemoveEventFromFeed}
          />
        ))}
      </div>
      <CtaFeedCard />
    </div>
  );
};

const CtaFeedCard = () => {
  const navigate = useNavigate();
  const { theme } = useEventStore();

  const ctaFeedCardDetails = {
    onClick: () => {
      navigate("/feed/v2", { state: { openCreatePost: true } });
    },
    ctaMessage: (
      <FormattedMessage
        id="feed.ctaPost.ctaButton"
        defaultMessage="Está gostando do evento? Poste uma foto você tambem!"
      />
    ),
  };

  const { onClick, ctaMessage } = ctaFeedCardDetails;

  return (
    <div className={`${theme.card} card w-full py-6 relative space-y-4`}>
      <div className="flex gap-4 px-6 items-center">
        <div className="w-full flex justify-center whitespace-normal h-min">
          <CtaButton theme={theme.buttonSecondary} onClick={onClick}>
            {ctaMessage}
          </CtaButton>
        </div>
      </div>
    </div>
  );
};

type THeartButtonProps = {
  liked: boolean;
  onClick: () => void;
};

const HeartButton = (props: THeartButtonProps) => {
  const { liked, onClick } = props;

  return (
    <button
      className={`flex items-center justify-center p-0 m-0`}
      onClick={onClick}
    >
      {liked ? (
        <Heart width={24} height={24} color="#F76C6F" outline="#F76C6F" />
      ) : (
        <Heart width={24} height={24} color="none" outline="#414141" />
      )}
    </button>
  );
};

const SingleImageContainer = ({
  url,
  imageAlt,
}: {
  url: string;
  imageAlt: string;
}) => {
  return (
    <div className={`col-span-2 w-full h-full`}>
      <img
        src={url}
        alt={imageAlt}
        className="object-cover w-full h-full max-w-full"
      />
    </div>
  );
};

const FeedEventCard = ({
  event: feedEvent,
  onRemoveEventFromFeed,
}: {
  event: TFeedEvent;
  onRemoveEventFromFeed: (eventId: string) => void;
}) => {
  const intl = useIntl();
  const { theme } = useEventStore();
  const { guest } = useGuestStore();
  const { event } = useEventStore();
  const anonymousGuest = intl.formatMessage({
    id: "feed.anonymousGuest",
    defaultMessage: "Convidado anônimo",
  });

  const [likedByGuest, setLikedByGuest] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const likeInitialized = useRef(false);
  const navigate = useNavigate();
  const { setTimeToReleaseChoice } = useTimeToReleaseUploadStore();
  const { updateExistingEvents, removeEventFromCache } = useFeedStore();

  const handleRecorderTimeCapsuleCtaClick = useCallback(() => {
    setTimeToReleaseChoice("release-later");
    navigate("/record/form");
  }, [setTimeToReleaseChoice, navigate]);

  const handleRecorderNowCtaClick = useCallback(() => {
    setTimeToReleaseChoice("release-now");
    navigate("/record/form");
  }, [setTimeToReleaseChoice, navigate]);

  const getBingoResultTypeText = useCallback((resultType: string) => {
    switch (resultType) {
      case "line":
        return (
          <FormattedMessage
            id="feed.bingo.result.line"
            defaultMessage="Linha"
          />
        );
      case "column":
        return (
          <FormattedMessage
            id="feed.bingo.result.column"
            defaultMessage="Coluna"
          />
        );
      case "fullCard":
        return (
          <FormattedMessage
            id="feed.bingo.result.fullCard"
            defaultMessage="Cartela Cheia"
          />
        );
      default:
        return resultType;
    }
  }, []);

  const feedDetails = useMemo(() => {
    switch (feedEvent.type) {
      case "image-uploader":
        return {
          title: (
            <FormattedMessage
              id="feed.imageUploader.title"
              defaultMessage="Foto Enviada"
            />
          ),
          description: (
            <FormattedMessage
              id="feed.imageUploader.description"
              defaultMessage="{guestName} enviou arquivos para galeria da festa"
              values={{ guestName: feedEvent.guestName || anonymousGuest }}
            />
          ),
        };
      case "cdt-image-uploader":
        return {
          title: (
            <FormattedMessage
              id="feed.cdt-imageUploader.title"
              defaultMessage="Nova Mensagem"
            />
          ),
          description: (
            <FormattedMessage
              id="feed.cdt-imageUploader.description"
              defaultMessage="{guestName} enviou arquivos para as Bodas de 1 Ano!"
              values={{ guestName: feedEvent.guestName || anonymousGuest }}
            />
          ),
        };
      case "quiz-result":
        return {
          title: (
            <FormattedMessage id="feed.quiz.title" defaultMessage="Quiz" />
          ),
          description: (
            <FormattedMessage
              id="feed.quiz.description"
              defaultMessage="{guestName} completou o quiz! Resultado: {score}/{total} em {time} segundos"
              values={{
                guestName: feedEvent.guestName || anonymousGuest,
                score: feedEvent.score,
                total: feedEvent.totalQuestions,
                time: feedEvent.timeSpentInSeconds,
              }}
            />
          ),
          ctaButton:
            guest?.id !== feedEvent.guestId ? (
              <CtaButton
                theme={theme.button}
                onClick={() => {
                  navigate("/games/quiz");
                }}
              >
                <FormattedMessage
                  id="feed.quiz.ctaButton"
                  defaultMessage="Teste seus conhecimentos"
                />
              </CtaButton>
            ) : null,
        };
      case "photo-challenge-done":
        return {
          title: (
            <FormattedMessage
              id="feed.photoChallenge.title"
              defaultMessage="Foto Desafio"
            />
          ),
          description: (
            <FormattedMessage
              id="feed.photoChallenge.description"
              defaultMessage="{guestName} completou o desafio: {challenge}"
              values={{
                guestName: feedEvent.guestName || anonymousGuest,
                challenge: feedEvent.challengeText,
              }}
            />
          ),
          ctaButton:
            guest?.id !== feedEvent.guestId ? (
              <CtaButton
                theme={theme.button}
                onClick={() => {
                  navigate("/games/photo-challenge");
                }}
              >
                <FormattedMessage
                  id="feed.photoChallenge.ctaButton"
                  defaultMessage="Realizar um desafio também"
                />
              </CtaButton>
            ) : null,
        };
      case "team-match-result":
        return {
          title: (
            <FormattedMessage
              id="feed.teamMatch.title"
              defaultMessage="Team Match"
            />
          ),
          description: (
            <FormattedMessage
              id="feed.teamMatch.description"
              defaultMessage="{guestName} participou do Team Match. Resultado: {teamAName} {scoreA} x {scoreB} {teamBName}"
              values={{
                guestName: feedEvent.guestName || anonymousGuest,
                teamAName: feedEvent.teamAName,
                teamBName: feedEvent.teamBName,
                scoreA: feedEvent.teamAScore,
                scoreB: feedEvent.teamBScore,
              }}
            />
          ),
          ctaButton:
            guest?.id !== feedEvent.guestId ? (
              <CtaButton
                theme={theme.button}
                onClick={() => {
                  navigate("/games/team-match");
                }}
              >
                <FormattedMessage
                  id="feed.teamMatch.ctaButton"
                  defaultMessage="Participar do Team Match"
                />
              </CtaButton>
            ) : null,
        };
      case "bingo-result":
        return {
          title: (
            <FormattedMessage id="feed.bingo.title" defaultMessage="Bingo" />
          ),
          description: (
            <FormattedMessage
              id="feed.bingo.description"
              defaultMessage="{guestName} completou uma {challenge} do bingo"
              values={{
                guestName: feedEvent.guestName || anonymousGuest,
                challenge: getBingoResultTypeText(feedEvent.resultType),
              }}
            />
          ),
          ctaButton:
            guest?.id !== feedEvent.guestId ? (
              <CtaButton
                theme={theme.button}
                onClick={() => {
                  navigate("/games/bingo");
                }}
              >
                <FormattedMessage
                  id="feed.bingo.ctaButton"
                  defaultMessage="Jogar bingo também"
                />
              </CtaButton>
            ) : null,
        };
      case "new-guest":
        return {
          title: (
            <FormattedMessage
              id="feed.newGuest.title"
              defaultMessage="Novo Convidado"
            />
          ),
          description: (
            <FormattedMessage
              id="feed.newGuest.description"
              defaultMessage="{guestName} se juntou ao evento"
              values={{ guestName: feedEvent.guestName || anonymousGuest }}
            />
          ),
        };
      case "video-recorder":
        return {
          title: (
            <FormattedMessage
              id="feed.videoRecorder.title"
              defaultMessage="Nova Mensagem"
            />
          ),
          description: (
            <FormattedMessage
              id="feed.videoRecorder.description"
              defaultMessage="{guestName} gravou uma nova mensagem"
              values={{ guestName: feedEvent.guestName || anonymousGuest }}
            />
          ),
          ctaButton:
            guest?.id !== feedEvent.guestId ? (
              <CtaButton
                theme={theme.button}
                onClick={handleRecorderNowCtaClick}
              >
                <FormattedMessage
                  id="feed.videoRecorder.ctaButton"
                  defaultMessage="Grave uma mensagem agora"
                />
              </CtaButton>
            ) : null,
        };
      case "video-uploader":
        return {
          title: (
            <FormattedMessage
              id="feed.videoUploader.title"
              defaultMessage="Nova Mensagem"
            />
          ),
          description: (
            <FormattedMessage
              id="feed.videoUploader.description"
              defaultMessage="{guestName} enviou um vídeo para galeria da festa"
              values={{ guestName: feedEvent.guestName || anonymousGuest }}
            />
          ),
        };
      case "cdt-video-uploader":
        return {
          title: (
            <FormattedMessage
              id="feed.cdt-videoUploader.title"
              defaultMessage="Nova Mensagem"
            />
          ),
          description: (
            <FormattedMessage
              id="feed.cdt-videoUploader.description"
              defaultMessage="{guestName} enviou um vídeo para as Bodas de 1 Ano!"
              values={{ guestName: feedEvent.guestName || anonymousGuest }}
            />
          ),
        };
      case "new-post":
        return {
          title: (
            <FormattedMessage
              id="feed.newPost.title"
              defaultMessage="Novo Post"
            />
          ),
          description: feedEvent.postText || "",
        };
      case "cdt-video-recorder":
        return {
          description: (
            <FormattedMessage
              id="feed.cdtVideoRecorder.description"
              defaultMessage="<code>{guestName}</code> já deixou sua mensagem para as <code> bodas de 1 ano!</code>"
              values={{
                guestName: feedEvent.guestName || anonymousGuest,
                code: (text) => (
                  <span className="font-century-gothic-bold">{text}</span>
                ),
              }}
            />
          ),
          ctaButton:
            guest?.id !== feedEvent.guestId ? (
              <CtaButton
                theme={theme.button}
                onClick={handleRecorderTimeCapsuleCtaClick}
              >
                <FormattedMessage
                  id="feed.cdtVideoRecorder.ctaButton"
                  defaultMessage="Faça o mesmo!"
                />
              </CtaButton>
            ) : null,
        };
      default:
        return {
          title: (
            <FormattedMessage
              id="feed.default.title"
              defaultMessage="Atividade"
            />
          ),
          description: (
            <FormattedMessage
              id="feed.default.description"
              defaultMessage="Uma nova atividade ocorreu"
            />
          ),
        };
    }
  }, [
    feedEvent,
    anonymousGuest,
    theme.button,
    navigate,
    handleRecorderNowCtaClick,
    handleRecorderTimeCapsuleCtaClick,
    getBingoResultTypeText,
    guest,
  ]);

  const { description, ctaButton } = feedDetails;

  let imageUrls: (string | null)[] = [];
  let imageAlt = "";

  switch (feedEvent.type) {
    case "image-uploader":
      if (event?.publicGallery) {
        imageUrls =
          feedEvent.fileUrls?.filter((url): url is string => url !== null) ||
          [];

        imageAlt = intl.formatMessage({
          id: "feed.imageAlt",
          defaultMessage: "Imagem Enviada",
        });
      } else {
        imageUrls = [null];
      }

      break;

    case "photo-challenge-done":
      imageUrls = feedEvent.photoUrl ? [feedEvent.photoUrl] : [];
      imageAlt = feedEvent.challengeText;
      break;

    case "new-post":
      imageUrls = feedEvent.postImageUrl ? [feedEvent.postImageUrl] : [];
      imageAlt = feedEvent.postText || "Novo post";
      break;

    case "bingo-result":
      if (event?.publicGallery) {
        imageUrls =
          feedEvent.challengesData
            ?.map((challenge) => challenge.photoUrl)
            .filter((url): url is string => url != null) || [];

        imageAlt = intl.formatMessage({
          id: "feed.imageAlt",
          defaultMessage: "Imagem Enviada",
        });
      } else {
        imageUrls = [null];
      }
      break;

    default:
      imageUrls = [];
      break;
  }

  const handleLikeToggle = async () => {
    if (!guest) return;

    const liked = !likedByGuest;
    setLikedByGuest(liked);

    if (clarity.hasStarted()) {
      clarity.setTag("feed_like", liked ? "liked" : "unliked");
      clarity.setTag("engagement_level_liker", "true");
    }

    const cdtAppClient = new CdtAppPublicApi();
    const params = {
      guestId: guest.id as string,
      guestName: guest.name,
    };
    const updatedEvent: TFeedEvent = {
      ...feedEvent,
      updatedAt: new Date().toISOString(),
      likes: liked
        ? [...feedEvent.likes, guest.id as string]
        : feedEvent.likes.filter((id) => id !== guest.id),
      totalLikes: liked ? feedEvent.totalLikes + 1 : feedEvent.totalLikes - 1,
    };

    if (event?.code) {
      updateExistingEvents(event.code, [updatedEvent]);
    }

    if (liked) {
      feedEvent.likes.push(guest.id as string);
      feedEvent.totalLikes += 1;

      await cdtAppClient.feedV2EventLike(
        feedEvent.eventCode,
        feedEvent.id,
        params
      );
    } else {
      const likeIndex = feedEvent.likes.indexOf(guest.id as string);

      if (likeIndex > -1) {
        feedEvent.likes.splice(likeIndex, 1);
        feedEvent.totalLikes -= 1;
      }

      await cdtAppClient.feedV2EventDislike(
        feedEvent.eventCode,
        feedEvent.id,
        params
      );
    }
  };

  const handleShowDeleteConfirmation = () => {
    setIsDeleteModalOpen(true);
  };

  const handleDeleteFeedPost = async () => {
    if (!event?.code || !guest?.id) return;

    const cdtAppClient = new CdtAppPublicApi();

    try {
      await cdtAppClient.feedV2EventDelete(event.code, feedEvent.id, {
        guestId: guest?.id,
        guestName: guest?.name,
      });

      removeEventFromCache(event.code, feedEvent.id);

      onRemoveEventFromFeed(feedEvent.id);
    } catch (error) {
      console.error("Error deleting feed post:", error);
    }
  };

  useEffect(() => {
    if (!likeInitialized.current && guest && feedEvent) {
      const liked = feedEvent.likes.includes(guest.id as string);
      setLikedByGuest(liked);

      likeInitialized.current = true;
    }
  }, [likeInitialized, feedEvent, guest]);

  return feedEvent.type !== "cdt-video-recorder" ? (
    <div className={`${theme.card} card w-full py-3 relative space-y-2`}>
      <div className="flex gap-2 px-3 items-center">
        <div className="w-14 h-14 grow-0 shrink-0 rounded-full overflow-hidden border-2 border-gray-200">
          {feedEvent.guestPhotoUrl ? (
            <img
              src={feedEvent.guestPhotoUrl}
              alt={feedEvent.guestName || "Guest"}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <User width={24} height={24} color="none" outline="#414141" />
            </div>
          )}
        </div>
        <div className="flex flex-col">
          <h3 className="text-base text-left font-century-gothic-bold">
            {feedEvent.guestName ? feedEvent.guestName : anonymousGuest}
          </h3>
          <p className="text-xs text-gray-500 text-left">
            {
              <FormattedDate
                value={feedEvent.createdAt}
                month="short"
                day="numeric"
              />
            }{" "}
            {<FormattedTime value={feedEvent.createdAt} />}
          </p>
        </div>
        {feedEvent.guestId === guest?.id && feedEvent.type === "new-post" && (
          <div className="justify-end self-start mt-1 flex flex-1">
            <button className="p-0" onClick={handleShowDeleteConfirmation}>
              <Trash width={20} height={20} color="none" outline="#414141" />
            </button>
          </div>
        )}
      </div>

      {imageUrls.length > 0 && imageUrls.every((url) => url !== null) && (
        <div className="relative mb-4 grid grid-cols-2 gap-1">
          {imageUrls.slice(0, 6).map((url, index) => {
            if (imageUrls.length === 1 && url) {
              return (
                <SingleImageContainer
                  key={index}
                  url={url}
                  imageAlt={imageAlt}
                />
              );
            }
            const dynamicClass =
              imageUrls.length === 2
                ? `col-span-2 w-full h-60`
                : imageUrls.length === 3
                ? index === 0
                  ? `col-span-2 w-full h-60`
                  : `col-span-1 w-full h-60`
                : imageUrls.length === 4
                ? `col-span-1 w-full h-60`
                : imageUrls.length === 5
                ? index === 0
                  ? `col-span-2 w-full h-48`
                  : `col-span-1 w-full h-48`
                : imageUrls.length === 6
                ? `col-span-1 w-full h-48`
                : imageUrls.length >= 7
                ? index >= 6
                  ? `hidden`
                  : `col-span-1 w-full h-48 `
                : "";

            return (
              <div
                key={index}
                className={`relative ${dynamicClass} ${
                  index === 6 && imageUrls.length > 6
                    ? "bg-black bg-opacity-50"
                    : ""
                }`}
              >
                {url && (
                  <img
                    src={url}
                    alt={imageAlt}
                    className="object-cover w-full h-full max-w-full"
                  />
                )}

                {index === 5 && imageUrls.length > 6 && (
                  <div className="absolute inset-0 z-10 bg-black bg-opacity-50 flex items-center justify-center">
                    <p className="text-white text-lg font-bold border-2 border-white rounded-full h-10 w-10 flex items-center justify-center">
                      +{imageUrls.length - 6}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex gap-2 px-4 items-end">
        <div className="flex-1">
          <h3 className={`text-left font-century-gothic text-base`}>
            {description}
          </h3>
        </div>
        {!ctaButton && (
          <div className="flex items-center gap-1 justify-end">
            <HeartButton liked={likedByGuest} onClick={handleLikeToggle} />

            {feedEvent.totalLikes > 0 && (
              <p className="text-sm text-gray-500 text-left">
                {feedEvent.totalLikes}
              </p>
            )}
          </div>
        )}
      </div>
      {ctaButton ? (
        <div className="w-full flex justify-center"> {ctaButton} </div>
      ) : null}
      {isDeleteModalOpen && (
        <FeedModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
        >
          <div className="p-4 flex flex-col items-center justify-center">
            <h2 className="text-xl font-century-gothic-bold mb-4">
              <FormattedMessage
                id="feed.deletePost.title"
                defaultMessage="Excluir Post"
              />
            </h2>
            <h3 className="font-century-gothic text-base mb-6 text-center">
              <FormattedMessage
                id="feed.deletePost.message"
                defaultMessage="Tem certeza que deseja excluir este post? Esta ação não pode ser desfeita."
              />
            </h3>
            <div className="flex gap-4">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className={` ${theme.button} button`}
              >
                <FormattedMessage
                  id="feed.button.options.cancel"
                  defaultMessage="Cancelar"
                />
              </button>
              <button
                onClick={handleDeleteFeedPost}
                className={` ${theme.buttonSecondary} button`}
              >
                <FormattedMessage
                  id="feed.button.options.confirm"
                  defaultMessage="Confirmar"
                />
              </button>
            </div>
          </div>
        </FeedModal>
      )}
    </div>
  ) : (
    <div
      className={`${theme.card} card w-full pt-0 py-6 space-y-4 relative overflow-hidden`}
    >
      <div
        className={`${
          theme.name === "rose" ? "bg-primary-rose" : "bg-black"
        }  w-full flex items-center justify-center h-[44px]`}
      >
        <img src={cdtWhiteLogo} alt="Capsula do Tempo" className="h-[30px]" />
      </div>
      <div className=" flex flex-col gap-4">
        <div className=" px-6 text-center">{description}</div>
        {ctaButton ? (
          <div className="w-full flex justify-center">{ctaButton}</div>
        ) : null}
      </div>
    </div>
  );
};
